/**
 * Gesprochene Agentennamen auf registrierte Namen abbilden (#1037).
 *
 * Alex, 2026-08-11: „Wir brauchen einen API-Endpoint, der über eine geschickte
 * Ähnlichkeitssuche Namen auflösen kann, da die reine Sprachkonvertierung mit
 * dem Agenten nicht passend ist. […] aber nicht über eine KI, sondern über eine
 * semantische Ähnlichkeitssuche vom Namen her."
 *
 * DER KERN IST NICHT DER ALGORITHMUS, SONDERN DIE SCHWELLE. Ein Namensauflöser,
 * der immer etwas zurückgibt, schickt Alex' Nachricht irgendwann an den falschen
 * Agenten — und das merkt niemand, weil der falsche Agent brav antwortet. Deshalb
 * gibt es drei Ausgänge statt zweier: `unique`, `ambiguous`, `none`. Bei
 * `ambiguous` wird zurückgefragt, nicht gewählt.
 *
 * Ausdrücklich ohne Modell: deterministisch, in Millisekunden, ohne Kosten. Eine
 * Auflösung, die bei gleicher Eingabe zweimal Verschiedenes liefert, ist
 * schlimmer als eine, die zugibt, dass sie es nicht weiß.
 *
 * Diese Datei ist zugleich die Referenz für den serverseitigen Endpunkt
 * (cloud-api, Issue #1037) und für iOS: dieselbe Faltung, dieselben Schwellen.
 * Weicht eine Seite ab, löst dieselbe Aussage je nach Gerät anders auf.
 */

/** Zahlwörter, die in Namen tatsächlich vorkommen. Englische Homophone
 * („to", „too", „for") sind bewusst NICHT dabei: sie sind in gesprochener Sprache
 * häufiger Präposition als Ziffer, und ein falsch eingesetztes „2" zerstört den
 * Namen, statt ihn zu retten. */
const NUMBER_WORDS: Record<string, string> = {
  null: '0', zero: '0',
  eins: '1', ein: '1', eine: '1', one: '1',
  zwei: '2', zwo: '2', two: '2',
  drei: '3', three: '3',
  vier: '4', four: '4',
  fuenf: '5', five: '5',
  sechs: '6', six: '6',
  sieben: '7', seven: '7',
  acht: '8', eight: '8',
  neun: '9', nine: '9',
  zehn: '10', ten: '10',
};

const UMLAUTS: Array<[RegExp, string]> = [
  [/ä/g, 'ae'], [/ö/g, 'oe'], [/ü/g, 'ue'], [/ß/g, 'ss'],
];

/**
 * Die gemeinsame Faltung. Beide Seiten MÜSSEN identisch falten.
 *
 * „drei D API" → `3dapi` · „Cloud V zwei" → `cloudv2` · „S W F M E" → `swfme`
 *
 * Zahlwörter werden nur als ganze Wörter ersetzt — sonst würde aus „Sabrina"
 * mit genug Fantasie eine Ziffer.
 */
export function foldSpokenName(value: string): string {
  // Reihenfolge zählt: Umlaute ZUERST ausschreiben. Wer vorher zerlegt und die
  // Zeichen entfernt, macht aus „Förderungen" ein „forderungen" — und trifft
  // den registrierten Namen dann nicht mehr.
  let text = (value || '').toLowerCase();
  for (const [pattern, replacement] of UMLAUTS) text = text.replace(pattern, replacement);
  text = text.normalize('NFKD').replace(/[̀-ͯ]/g, '');
  text = text
    .split(/[^a-z0-9]+/)
    .map((token) => NUMBER_WORDS[token] ?? token)
    .join('');
  return text.replace(/[^a-z0-9]/g, '');
}

/**
 * Kölner Phonetik — die deutsche Antwort auf Soundex.
 *
 * Bewusst nicht Soundex: das ist auf englische Aussprache getrimmt und wirft
 * `Tschepp` und `Tschauko` in denselben Topf, während es `Kitt`/`Kaebi` trennt,
 * wo es nicht nötig wäre. Ziffern tragen hier nichts bei — sie sind nach der
 * Faltung ohnehin exakt — und werden übersprungen.
 */
export function koelnerPhonetik(value: string): string {
  const s = foldSpokenName(value).replace(/[0-9]/g, '');
  const codes: string[] = [];
  // `''.includes` ist immer wahr — am Wortende hätte jede Nachbarschaftsregel
  // gegriffen und aus dem letzten „t" eine 8 gemacht. Deshalb prüft `follows`
  // erst, ob es überhaupt einen Nachbarn gibt.
  const follows = (set: string, ch: string) => Boolean(ch) && set.includes(ch);
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    const next = s[i + 1] || '';
    const prev = s[i - 1] || '';
    let code = '';
    switch (c) {
      case 'a': case 'e': case 'i': case 'j': case 'o': case 'u': case 'y': code = '0'; break;
      case 'b': code = '1'; break;
      case 'p': code = next === 'h' ? '3' : '1'; break;
      case 'd': case 't': code = follows('csz', next) ? '8' : '2'; break;
      case 'f': case 'v': case 'w': code = '3'; break;
      case 'g': case 'k': case 'q': code = '4'; break;
      case 'c':
        if (i === 0) code = follows('ahkloqrux', next) ? '4' : '8';
        else if (follows('sz', prev)) code = '8';
        else code = follows('ahkoqux', next) ? '4' : '8';
        break;
      case 'x': code = follows('ckq', prev) ? '8' : '48'; break;
      case 'l': code = '5'; break;
      case 'm': case 'n': code = '6'; break;
      case 'r': code = '7'; break;
      case 's': case 'z': code = '8'; break;
      default: code = ''; // h und alles Übrige tragen keinen Code
    }
    for (const digit of code) {
      if (digit !== codes[codes.length - 1]) codes.push(digit);
    }
  }
  // Nullen entfallen, außer der führenden: sie steht für den Anlaut.
  return codes.filter((digit, index) => index === 0 || digit !== '0').join('');
}

/** Jaro-Winkler. Die Präfix-Gewichtung passt zu Namen: der Anfang wird beim
 * Sprechen am zuverlässigsten erkannt, das Ende verschluckt. */
export function jaroWinkler(a: string, b: string): number {
  if (a === b) return 1;
  if (!a.length || !b.length) return 0;
  const window = Math.max(0, Math.floor(Math.max(a.length, b.length) / 2) - 1);
  const aMatched = new Array<boolean>(a.length).fill(false);
  const bMatched = new Array<boolean>(b.length).fill(false);
  let matches = 0;
  for (let i = 0; i < a.length; i += 1) {
    const start = Math.max(0, i - window);
    const end = Math.min(i + window + 1, b.length);
    for (let j = start; j < end; j += 1) {
      if (bMatched[j] || a[i] !== b[j]) continue;
      aMatched[i] = true; bMatched[j] = true; matches += 1;
      break;
    }
  }
  if (!matches) return 0;
  let transpositions = 0;
  let k = 0;
  for (let i = 0; i < a.length; i += 1) {
    if (!aMatched[i]) continue;
    while (!bMatched[k]) k += 1;
    if (a[i] !== b[k]) transpositions += 1;
    k += 1;
  }
  const m = matches;
  const jaro = (m / a.length + m / b.length + (m - transpositions / 2) / m) / 3;
  let prefix = 0;
  while (prefix < 4 && prefix < a.length && prefix < b.length && a[prefix] === b[prefix]) prefix += 1;
  return jaro + prefix * 0.1 * (1 - jaro);
}

export type MatchReason = 'exact' | 'exact_fold' | 'phonetic' | 'prefix' | 'fuzzy';

export interface AgentCandidate {
  name: string;
  /** Höher gewinnt bei GLEICHEM Zeichenwert — serverseitig aus Zustand
   * (laufend > ausgesetzt) und Besitz. Der Client kennt das meist nicht. */
  priority?: number;
}

export interface ScoredCandidate {
  name: string;
  score: number;
  reason: MatchReason;
}

export type NameResolution =
  | { decision: 'unique'; match: ScoredCandidate; candidates: ScoredCandidate[] }
  | { decision: 'ambiguous'; match: null; candidates: ScoredCandidate[] }
  | { decision: 'none'; match: null; candidates: ScoredCandidate[] };

/** Ab hier gilt ein Treffer überhaupt als Treffer. */
export const MATCH_THRESHOLD = 0.86;
/** Und so weit muss er vom Zweiten entfernt sein, damit er allein dasteht.
 * Ohne diesen Abstand würde `GuideDevBot` gegen `GuideDevBot2` per Münzwurf
 * entschieden — und der Münzwurf fällt jedes Mal gleich, also immer falsch. */
export const MATCH_MARGIN = 0.06;

function scoreOne(foldedSpoken: string, spoken: string, candidate: string): ScoredCandidate {
  if (spoken === candidate) return { name: candidate, score: 1, reason: 'exact' };
  const folded = foldSpokenName(candidate);
  if (folded === foldedSpoken) return { name: candidate, score: 1, reason: 'exact_fold' };

  let score = jaroWinkler(foldedSpoken, folded);
  let reason: MatchReason = 'fuzzy';
  if (folded.startsWith(foldedSpoken) || foldedSpoken.startsWith(folded)) {
    score += 0.05;
    reason = 'prefix';
  }
  if (koelnerPhonetik(foldedSpoken) === koelnerPhonetik(folded)) {
    score += 0.1;
    reason = reason === 'prefix' ? 'prefix' : 'phonetic';
  }
  return { name: candidate, score: Math.min(score, 0.999), reason };
}

/**
 * Löst einen gesprochenen Namen auf — oder sagt ehrlich, dass er es nicht kann.
 *
 * Die Reihenfolge der Ausgänge ist wichtiger als ihre Berechnung:
 * - `unique` nur bei Wert über der Schwelle UND klarem Abstand zum Zweiten
 * - `ambiguous` wenn mehrere gleich gut passen → der Aufrufer FRAGT ZURÜCK
 * - `none` wenn nichts nah genug ist → keine Erfindung
 */
export function resolveSpokenAgentName(
  spoken: string,
  candidates: readonly (string | AgentCandidate)[],
  options: { threshold?: number; margin?: number; limit?: number } = {},
): NameResolution {
  const threshold = options.threshold ?? MATCH_THRESHOLD;
  const margin = options.margin ?? MATCH_MARGIN;
  const limit = options.limit ?? 5;
  const trimmed = (spoken || '').trim();
  const folded = foldSpokenName(trimmed);
  if (!folded) return { decision: 'none', match: null, candidates: [] };

  const priority = new Map<string, number>();
  const names: string[] = [];
  for (const entry of candidates) {
    const name = typeof entry === 'string' ? entry : entry.name;
    if (!name || names.includes(name)) continue;
    names.push(name);
    if (typeof entry !== 'string' && entry.priority != null) priority.set(name, entry.priority);
  }

  const scored = names
    .map((name) => scoreOne(folded, trimmed, name))
    .filter((entry) => entry.score >= threshold - margin)
    .sort((a, b) => b.score - a.score
      || (priority.get(b.name) ?? 0) - (priority.get(a.name) ?? 0)
      || a.name.localeCompare(b.name))
    .slice(0, limit);

  const best = scored[0];
  if (!best || best.score < threshold) return { decision: 'none', match: null, candidates: scored };

  // Ein Volltreffer ist etwas anderes als ein guter Treffer, und der
  // Abstandsregel darf er nicht zum Opfer fallen: „Cloud V zwei" trifft
  // `CloudV2` GENAU — dass `CloudV2-Codex` daneben fast so gut aussieht, macht
  // die Aussage nicht mehrdeutig. Mehrdeutig wird sie erst, wenn ZWEI Namen
  // exakt treffen (dann entscheidet der Rang, sonst wird gefragt).
  const exact = scored.filter((entry) => entry.reason === 'exact' || entry.reason === 'exact_fold');
  if (exact.length === 1) return { decision: 'unique', match: exact[0], candidates: scored };
  if (exact.length > 1) {
    const [first, runnerUp] = exact;
    const better = (priority.get(first.name) ?? 0) > (priority.get(runnerUp.name) ?? 0);
    return better
      ? { decision: 'unique', match: first, candidates: scored }
      : { decision: 'ambiguous', match: null, candidates: exact };
  }

  const second = scored[1];
  if (second && best.score - second.score < margin) {
    // Gleichstand im Zeichenwert darf der Rang brechen — aber NUR wenn er
    // wirklich unterscheidet. Zwei gleich benannte, gleich eingestufte Agenten
    // bleiben mehrdeutig, statt alphabetisch ausgewürfelt zu werden.
    const bestPriority = priority.get(best.name) ?? 0;
    const secondPriority = priority.get(second.name) ?? 0;
    if (bestPriority <= secondPriority) {
      return { decision: 'ambiguous', match: null, candidates: scored };
    }
  }
  return { decision: 'unique', match: best, candidates: scored };
}
